// Workstream B — Obligations / Cost-Recovery ledger HTTP transport. A
// read-only view over the obligation rows the Payment System v3 money engine
// produces, both per-loan and cross-loan. Hooks in
// @/hooks/portfolioAdmin/useObligationLedger wrap these with TanStack Query.

import { api } from "@/api/client";

// --- Vocabulary ---------------------------------------------------------------

/** Obligation lifecycle state (mirrors the backend `ObligationState`). */
export type ObligationState =
  | "pending"
  | "partially_paid"
  | "paid_in_full"
  | "waived"
  | "charged_off"
  | "superseded";

/** Who settles the obligation's disbursements (mirrors `SettlementChannel`). */
export type SettlementChannel = "servicer" | "phoenix";

// --- DTOs ---------------------------------------------------------------------

export interface ObligationLedgerRowDto {
  obligationId: string;
  loanId: string | null;
  loanNumber: string | null;
  typeCode: string;
  typeDisplay: string;
  waterfallPriority: number | null;
  state: ObligationState;
  settlementChannel: SettlementChannel | null;
  originalAmount: number;
  recoveredToDate: number;
  remaining: number;
  totalPaidLifetime: number;
  /** Counterparty stamped at creation; null for servicing obligations (the
   * disbursement carries the resolved recipient). */
  recipientEntityId?: string | null;
  recipientName?: string | null;
  /** Natural-key period slot; null for fee/event obligations without one. */
  periodStart?: string | null;
  /** Layperson description from the ObligationType registry row. */
  typeDescription?: string | null;
  totalCap?: number | null;
  /** ObligationType creation mode (each_period, event_triggered, …). */
  creationMode?: string | null;
}

export interface ObligationLedgerSummaryDto {
  count: number;
  totalOriginal: number;
  totalRecovered: number;
  totalRemaining: number;
}

export interface LoanObligationLedgerDto {
  loanId: string;
  loanNumber: string;
  obligations: ObligationLedgerRowDto[];
  summary: ObligationLedgerSummaryDto;
}

export interface CostRecoveryLedgerDto {
  rows: ObligationLedgerRowDto[];
  summary: ObligationLedgerSummaryDto;
}

export interface CostRecoveryLedgerParams {
  settlementChannel?: string;
  state?: string;
}

// --- Guided waive (PR-2) --------------------------------------------------------

export interface WaiveObligationRequest {
  reason: string;
}

/** What the waive actually did — the confirmation surface's facts. The
 * remaining balance is written off (recovered amounts stand); linked fee
 * rows resolve alongside, and a billed linked fee on a still-regenerable
 * statement triggers a regeneration. */
export interface ObligationWaiveResultDto {
  obligationId: string;
  waivedAmount: number;
  recoveredKept: number;
  resolvedFeeIds: string[];
  regeneratedStatementIds: string[];
}

// --- PR-6 Explain Engine: detail + comparison + Ask-AI ------------------------

export interface ObligationCatalogFactsDto {
  typeCode: string;
  typeDisplay: string;
  description: string | null;
  creationMode: string | null;
  currentWaterfallNumber: number | null;
  currentWaterfallPriority: number | null;
  firesOnIncomeTypes: string | null;
  isTakeAllRemaining: boolean;
  isCurrentlyFireable: boolean;
}

export interface ObligationLoanRealityDto {
  state: ObligationState;
  originalAmount: number;
  outstandingBalance: number;
  totalPaidLifetime: number;
  totalCap: number | null;
  waterfallNumberAtCreation: number | null;
  waterfallPriorityAtCreation: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  recipientEntityId: string | null;
  recipientName: string | null;
  recipientNote: string;
}

export interface ObligationFeeLinkDto {
  feeId: string;
  sourceType: string | null;
  sourceReference: string | null;
  status: string;
  billedOnStatementId: string | null;
  workoutCostId: string | null;
  defaultCaseId: string | null;
  docsCertified: boolean | null;
}

export interface ObligationRetirementDto {
  changeType: string;
  changeAmount: number;
  balanceBefore: number;
  balanceAfter: number;
  at: string;
}

export interface ObligationDetailDisbursementDto {
  disbursementId: string;
  amount: number;
  isReversed: boolean;
  settlementChannel: string | null;
  recipientEntityId: string | null;
  at: string;
  provenance: string | null;
}

export interface ObligationAnomalyFlagDto {
  code: string;
  severity: "info" | "warning" | "error";
  explanation: string;
}

export interface ObligationDetailDto {
  obligationId: string;
  loanId: string | null;
  loanNumber: string | null;
  catalog: ObligationCatalogFactsDto;
  reality: ObligationLoanRealityDto;
  feeLink: ObligationFeeLinkDto | null;
  recentRetirements: ObligationRetirementDto[];
  disbursements: ObligationDetailDisbursementDto[];
  flags: ObligationAnomalyFlagDto[];
}

export interface ObligationPeriodChangeDto {
  typeCode: string;
  typeDisplay: string;
  kind: "new" | "grown" | "shrank" | "stopped" | "unchanged";
  currentAmount: number;
  priorAmount: number;
  delta: number;
  explanation: string;
}

export interface ObligationPeriodComparisonDto {
  loanId: string;
  loanNumber: string;
  currentPeriodStart: string | null;
  priorPeriodStart: string | null;
  currentTotalObligated: number;
  priorTotalObligated: number;
  changes: ObligationPeriodChangeDto[];
  projectedNextPeriodObligated: number;
  projectedNextPeriodIndex: number;
}

export interface ObligationExplainTurn {
  role: "user" | "assistant";
  content: string;
}

export interface ObligationExplainRequest {
  question: string | null;
  history: ObligationExplainTurn[] | null;
}

export interface ObligationExplainDto {
  answer: string;
}

// --- Transport ----------------------------------------------------------------

export const obligationLedgerApi = {
  forLoan: (loanId: string) =>
    api.get<LoanObligationLedgerDto>(`/portfolio-admin/loans/${loanId}/obligations`),
  crossLoan: (params?: CostRecoveryLedgerParams) =>
    api.get<CostRecoveryLedgerDto>(`/portfolio-admin/cost-recovery`, params),
  // PR-6 — the per-obligation detail feed (grounding for the drawer + explain).
  detail: (loanId: string, obligationId: string) =>
    api.get<ObligationDetailDto>(
      `/portfolio-admin/loans/${loanId}/obligations/${obligationId}/detail`),
  comparison: (loanId: string) =>
    api.get<ObligationPeriodComparisonDto>(
      `/portfolio-admin/loans/${loanId}/obligations/comparison`),
  // Ask-AI: read-only AI explanation (POST but purely a read).
  explain: (loanId: string, obligationId: string, body: ObligationExplainRequest) =>
    api.post<ObligationExplainDto>(
      `/portfolio-admin/loans/${loanId}/obligations/${obligationId}/explain`, body),
  // Guided waive — resolves BOTH representations (obligation + linked fee).
  // Gated Obligation.Waive server-side; refusals come back as 400 with the
  // explicit reason.
  waive: (loanId: string, obligationId: string, body: WaiveObligationRequest) =>
    api.post<ObligationWaiveResultDto>(
      `/portfolio-admin/loans/${loanId}/obligations/${obligationId}/waive`, body),
};
